import { markRaw } from 'vue';
import RootApiService from '@/services/api/RootApiService';
import PublicClubCard from '@/components/public/PublicClubCard/PublicClubCard.vue';
import { smallestImageUrl, imageSrcset } from '@/services/utils/imageVariants';
import { SERVICE_BRAND } from '@/config';
import heroImage from '@/assets/homepage-background.webp';
import markerImage from '@/assets/map-marker-2.webp';

export default {
  name: 'ClubGeographyMap',
  components: { PublicClubCard },
  data() {
    return { loading: false, error: false, ready: false, empty: false, unavailable: 0,
      selected: [], selectedIndex: 0, cities: [],
      configured: Boolean(import.meta.env.VITE_GOOGLE_MAPS_API_KEY && import.meta.env.VITE_GOOGLE_MAPS_MAP_ID) };
  },
  computed: {
    point() { return this.selected[this.selectedIndex]; },
    selectedClub() {
      const club = this.point.club;
      return { name: club.name, city: [club.city, club.country].filter(Boolean).join(', '),
        tournaments: club.tournaments || 0, players: club.players || 0,
        logo: smallestImageUrl(club.avatar) || SERVICE_BRAND.logo, logoSrcset: imageSrcset(club.avatar),
        background: club.background || heroImage, route: { name: 'ClubHome', params: { clubSlug: club.slug } } };
    },
  },
  mounted() {
    if (!this.configured) return;
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
    });
    this.observer.observe(this.$el);
  },
  beforeUnmount() {
    this.disposed = true;
    this.observer?.disconnect();
    this.destroyMap();
  },
  methods: {
    destroyMap() {
      this.clusterer?.clearMarkers();
      this.clusterer?.setMap(null);
      for (const marker of this.markers || []) { window.google?.maps?.event.clearInstanceListeners(marker); marker.map = null; }
      if (this.map) window.google?.maps?.event.clearInstanceListeners(this.map);
      this.map = null;
    },
    async load() {
      if (this.loading || this.disposed) return;
      this.loading = true; this.error = false;
      try {
        // Сначала точки: пустая география не расходует загрузку Google Maps.
        const response = await RootApiService.api.get('/public/home/map', { skipToken: true });
        if (this.disposed) return;
        const { points = [], unavailable = 0, cities = [] } = response.data.data;
        this.unavailable = unavailable;
        this.cities = cities;
        this.empty = !points.length;
        if (this.empty) return;
        const [{ loadGoogleMaps }, { MarkerClusterer }] = await Promise.all([
          import('@/services/googleMaps'), import('@googlemaps/markerclusterer'),
        ]);
        if (this.disposed) return;
        const maps = await loadGoogleMaps(this.$i18n.locale);
        if (this.disposed) return;
        this.destroyMap();
        this.map = markRaw(new maps.Map(this.$refs.canvas, {
          mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID,
          center: points[0].position, zoom: 11,
          colorScheme: document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'DARK' : 'LIGHT',
          mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
          gestureHandling: 'cooperative', minZoom: 2,
        }));
        const bounds = new maps.LatLngBounds();
        this.markers = points.map(point => {
          bounds.extend(point.position);
          const image = document.createElement('img');
          image.src = markerImage;
          image.alt = '';
          image.className = 'club-geography__marker-image';
          const pin = document.createElement('div');
          pin.className = 'club-geography__marker';
          pin.appendChild(image);
          const marker = new maps.marker.AdvancedMarkerElement({ position: point.position, title: `${point.club.name} — ${point.name}`, content: pin });
          marker.addListener('click', () => {
            this.selected = points.filter(item => item.position.lat === point.position.lat && item.position.lng === point.position.lng);
            this.selectedIndex = this.selected.indexOf(point);
          });
          marker.clubPoint = point;
          return marker;
        });
        this.clusterer = markRaw(new MarkerClusterer({
          map: this.map, markers: this.markers,
          renderer: { render: ({ count, position }) => {
            const badge = document.createElement('div');
            badge.textContent = String(count);
            badge.className = 'club-geography__cluster';
            return new maps.marker.AdvancedMarkerElement({ position, content: badge, title: `${this.$t('geography.points')}: ${count}`, zIndex: 1000 + count });
          } },
          onClusterClick: (_event, cluster, map) => {
            this.selected = cluster.markers.map(marker => marker.clubPoint);
            this.selectedIndex = 0;
            if (cluster.bounds) map.fitBounds(cluster.bounds, 70);
            maps.event.addListenerOnce(map, 'idle', () => { if (map.getZoom() > 17) map.setZoom(17); });
          },
        }));
        if (points.length > 1) {
          this.map.fitBounds(bounds, 60);
          maps.event.addListenerOnce(this.map, 'idle', () => { if (this.map && this.map.getZoom() > 12) this.map.setZoom(12); });
        }
        this.ready = true;
      } catch {
        if (!this.disposed) this.error = true;
      } finally { if (!this.disposed) this.loading = false; }
    },
  },
};
